import { cn } from "@/lib/utils"

type LabeledSwitchProps = {
  label: string
  hint?: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  className?: string
  /** card — рамка с отступом; inline — подпись и switch в строке; stacked — подпись над switch */
  layout?: "card" | "inline" | "stacked"
}

function SwitchTrack({
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  disabled: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-ui-accent" : "dark:bg-ui-surface-raised light:bg-clay-4",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </button>
  )
}

export function LabeledSwitch({
  label,
  hint,
  checked,
  onCheckedChange,
  disabled = false,
  className,
  layout = "card",
}: LabeledSwitchProps) {
  const track = <SwitchTrack label={label} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
  const caption = (
    <div className="min-w-0">
      <p className={cn("text-xs text-ui-text-secondary", layout === "card" && "font-semibold")}>{label}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-ui-text-muted">{hint}</p> : null}
    </div>
  )

  if (layout === "inline") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {caption}
        {track}
      </div>
    )
  }

  if (layout === "stacked") {
    return (
      <div className={cn("flex flex-col items-start gap-1", className)}>
        {caption}
        {track}
      </div>
    )
  }

  return (
    <div className={cn("rounded-lg border border-ui-border bg-ui-surface-sunken/40 p-4", className)}>
      <div className="flex items-center justify-between gap-3">
        {caption}
        {track}
      </div>
    </div>
  )
}
